import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MockInterviewGenerationSubTab.css';
import Toast from './Toast';

const API_URL = process.env.REACT_APP_API_URL || '';

function MockInterviewGenerationSubTab() {
  const [text, setText] = useState('');
  const [generatedQuestions, setGeneratedQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [showMockInterviewModal, setShowMockInterviewModal] = useState(false);
  const [regeneratingAnswerIndex, setRegeneratingAnswerIndex] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [mockInterviewForm, setMockInterviewForm] = useState({
    company_id: '',
    position: '',
    grade: 'Junior',
    salary_range: '',
    questions: []
  });

  useEffect(() => {
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/companies`);
      setCompanies(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке компаний:', err);
    }
  };

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const hideToast = () => {
    setToast(null);
  };

  const handleGenerate = async () => {
    if (!text.trim()) {
      setError('Введите текст для генерации вопросов');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/generate`, {
        text: text.trim()
      });
      // Backend возвращает массив строк (этап 1 двухэтапной генерации). Для mock-собеседования используем как текст вопроса, ответ пустой.
      const raw = response.data.questions || [];
      const questions = raw.map(q => (typeof q === 'string' ? { question: q, answer: '' } : { question: q.question || '', answer: q.correct_answer || '' }));
      
      setGeneratedQuestions(questions);
      showToast(`Сгенерировано вопросов: ${questions.length}`, 'success');
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      setError('Ошибка при генерации вопросов: ' + errorMessage);
      showToast('Ошибка при генерации вопросов: ' + errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionChange = (index, field, value) => {
    const updated = [...generatedQuestions];
    updated[index] = { ...updated[index], [field]: value };
    setGeneratedQuestions(updated);
  };

  const handleDeleteQuestion = (index) => {
    const updated = generatedQuestions.filter((_, i) => i !== index);
    setGeneratedQuestions(updated);
  };

  const handleAddQuestion = () => {
    setGeneratedQuestions([
      ...generatedQuestions,
      {
        question: '',
        answer: ''
      }
    ]);
  };

  const handleCreateMockInterview = () => {
    if (generatedQuestions.length === 0) {
      showToast('Добавьте хотя бы один вопрос', 'warning');
      return;
    }

    // Проверяем, что все вопросы заполнены
    const emptyQuestions = generatedQuestions.filter(
      q => !q.question.trim() || !q.answer.trim()
    );
    
    if (emptyQuestions.length > 0) {
      showToast('Заполните все поля для всех вопросов', 'warning');
      return;
    }

    setMockInterviewForm({
      company_id: '',
      position: '',
      grade: 'Junior',
      salary_range: '',
      questions: generatedQuestions.map(q => ({
        question: q.question.trim(),
        answer: q.answer.trim()
      }))
    });
    setShowMockInterviewModal(true);
  };

  const handleCloseMockInterviewModal = () => {
    setShowMockInterviewModal(false);
    setMockInterviewForm({
      company_id: '',
      position: '',
      grade: 'Junior',
      salary_range: '',
      questions: []
    });
  };

  const handleAddMockQuestion = () => {
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: [...mockInterviewForm.questions, { question: '', answer: '' }]
    });
  };

  const handleUpdateMockQuestion = (index, field, value) => {
    const updatedQuestions = [...mockInterviewForm.questions];
    updatedQuestions[index] = {
      ...updatedQuestions[index],
      [field]: value
    };
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: updatedQuestions
    });
  };

  const handleDeleteMockQuestion = (index) => {
    const updatedQuestions = mockInterviewForm.questions.filter((_, i) => i !== index);
    setMockInterviewForm({
      ...mockInterviewForm,
      questions: updatedQuestions
    });
  };

  const handleRegenerateMockAnswer = async (index) => {
    const questionText = (mockInterviewForm.questions[index]?.question || '').trim();
    if (!questionText) {
      showToast('Введите текст вопроса для пересоздания ответа', 'warning');
      return;
    }
    try {
      setRegeneratingAnswerIndex(index);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      const answer = response.data?.correct_answer ?? '';
      handleUpdateMockQuestion(index, 'answer', answer);
      showToast('Ответ пересоздан', 'success');
    } catch (err) {
      showToast('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message), 'error');
    } finally {
      setRegeneratingAnswerIndex(null);
    }
  };

  const handleSubmitMockInterview = async () => {
    // Валидация
    if (!mockInterviewForm.company_id) {
      showToast('Выберите компанию', 'warning');
      return;
    }
    if (!mockInterviewForm.position.trim()) {
      showToast('Введите название должности', 'warning');
      return;
    }
    if (!mockInterviewForm.salary_range.trim()) {
      showToast('Введите зарплатную вилку', 'warning');
      return;
    }
    if (mockInterviewForm.questions.length === 0) {
      showToast('Добавьте хотя бы один вопрос', 'warning');
      return;
    }

    // Проверяем, что все вопросы заполнены
    for (let i = 0; i < mockInterviewForm.questions.length; i++) {
      const q = mockInterviewForm.questions[i];
      if (!q.question.trim() || !q.answer.trim()) {
        showToast(`Заполните вопрос и ответ для вопроса ${i + 1}`, 'warning');
        return;
      }
    }

    try {
      setSaving(true);
      
      // Формируем данные для отправки
      const interviewData = {
        company_id: parseInt(mockInterviewForm.company_id),
        title: `${mockInterviewForm.position} - ${mockInterviewForm.grade}`,
        position: mockInterviewForm.position,
        grade: mockInterviewForm.grade,
        salary_range: mockInterviewForm.salary_range,
        questions: mockInterviewForm.questions.map(q => ({
          question: q.question.trim(),
          answer: q.answer.trim()
        }))
      };

      await axios.post(`${API_URL}/api/mock-interviews`, interviewData);
      
      showToast('Mock-собеседование успешно создано!', 'success');
      handleCloseMockInterviewModal();
      // Очищаем форму генерации
      setText('');
      setGeneratedQuestions([]);
    } catch (err) {
      const errorMessage = err.response?.data?.detail || err.message || 'Неизвестная ошибка';
      showToast('Ошибка при создании mock-собеседования: ' + errorMessage, 'error');
    } finally {
      setSaving(false);
    }
  };

  if (generatedQuestions.length === 0) {
    return (
      <div className="mock-interview-generation-container">
        {error && <div className="error-message">{error}</div>}
        {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
        
        <div className="form-container">
          <h3>Генерация вопросов для Mock-собеседования из текста</h3>
          <div className="form-group">
            <label htmlFor="text">Введите текст для анализа *</label>
            <textarea
              id="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              rows={12}
              placeholder="Вставьте текст, из которого нужно извлечь вопросы для mock-собеседования..."
              disabled={loading}
            />
          </div>
          <div className="form-actions">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleGenerate}
              disabled={loading || !text.trim()}
            >
              {loading ? 'Генерация...' : 'Сгенерировать вопросы'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mock-interview-generation-container">
      {error && <div className="error-message">{error}</div>}
      {toast && <Toast message={toast.message} type={toast.type} onClose={hideToast} />}
      
      <div className="generated-questions-header">
        <h3>Сгенерированные вопросы ({generatedQuestions.length})</h3>
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleAddQuestion}
          >
            ➕ Добавить вопрос
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleCreateMockInterview}
            disabled={generatedQuestions.length === 0}
          >
            🎯 Создать Mock-собеседование
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setText('');
              setGeneratedQuestions([]);
              setError(null);
            }}
          >
            🔄 Начать заново
          </button>
        </div>
      </div>

      <div className="generated-questions-list">
        {generatedQuestions.map((q, index) => (
          <div key={index} className="question-item-container">
            <div className="question-item-header">
              <span className="question-number">{index + 1}.</span>
              <div className="question-item-actions">
                <button
                  className="btn btn-tiny btn-delete"
                  onClick={() => handleDeleteQuestion(index)}
                >
                  🗑️ Удалить
                </button>
              </div>
            </div>
            <div className="question-edit-form">
              <div className="question-edit-field">
                <label>Вопрос:</label>
                <textarea
                  className="question-edit-textarea"
                  value={q.question}
                  onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                  rows={2}
                  placeholder="Введите вопрос..."
                />
              </div>
              <div className="question-edit-field">
                <label>Ответ:</label>
                <textarea
                  className="question-edit-textarea"
                  value={q.answer}
                  onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                  rows={3}
                  placeholder="Введите ответ..."
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Модальное окно создания Mock-собеседования */}
      {showMockInterviewModal && (
        <div className="modal-overlay" onClick={handleCloseMockInterviewModal}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Создать Mock-собеседование</h2>
              <button className="btn-icon btn-close" onClick={handleCloseMockInterviewModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label htmlFor="company_id">Компания *</label>
                <select
                  id="company_id"
                  value={mockInterviewForm.company_id}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, company_id: e.target.value })}
                  required
                  disabled={saving}
                >
                  <option value="">Выберите компанию</option>
                  {companies.map((company) => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="position">Название должности *</label>
                <input
                  type="text"
                  id="position"
                  value={mockInterviewForm.position}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, position: e.target.value })}
                  required
                  maxLength={255}
                  placeholder="Например: Backend Developer"
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <label htmlFor="grade">Грейд *</label>
                <select
                  id="grade"
                  value={mockInterviewForm.grade}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, grade: e.target.value })}
                  required
                  disabled={saving}
                >
                  <option value="Junior">Junior</option>
                  <option value="Middle">Middle</option>
                  <option value="Senior">Senior</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="salary_range">Зарплатная вилка *</label>
                <input
                  type="text"
                  id="salary_range"
                  value={mockInterviewForm.salary_range}
                  onChange={(e) => setMockInterviewForm({ ...mockInterviewForm, salary_range: e.target.value })}
                  required
                  maxLength={255}
                  placeholder="Например: 150000-250000 руб."
                  disabled={saving}
                />
              </div>

              <div className="form-group">
                <div className="detail-header">
                  <label>Вопросы и ответы:</label>
                  <button
                    className="btn btn-small btn-primary"
                    onClick={handleAddMockQuestion}
                    disabled={saving}
                  >
                    ➕ Добавить вопрос
                  </button>
                </div>
                {mockInterviewForm.questions.length === 0 ? (
                  <div className="summary-empty-state">
                    <p>Вопросы отсутствуют. Добавьте первый вопрос:</p>
                    <button
                      className="btn btn-primary"
                      onClick={handleAddMockQuestion}
                      disabled={saving}
                    >
                      ➕ Добавить вопрос
                    </button>
                  </div>
                ) : (
                  <div className="questions-list-container">
                    {mockInterviewForm.questions.map((item, index) => (
                      <div key={index} className="question-item-container">
                        <div className="question-item-header">
                          <span className="question-number">{index + 1}.</span>
                          <div className="question-item-actions">
                            <button
                              className="btn btn-tiny btn-delete"
                              onClick={() => handleDeleteMockQuestion(index)}
                              disabled={saving}
                            >
                              🗑️ Удалить
                            </button>
                          </div>
                        </div>
                        <div className="question-edit-form">
                          <div className="question-edit-field">
                            <label>Вопрос:</label>
                            <textarea
                              className="question-edit-textarea"
                              value={item.question}
                              onChange={(e) => handleUpdateMockQuestion(index, 'question', e.target.value)}
                              rows={2}
                              disabled={saving}
                              placeholder="Введите вопрос..."
                            />
                          </div>
                          <div className="question-edit-field">
                            <div className="form-group-label-row">
                              <label>Ответ:</label>
                              <button
                                type="button"
                                className="btn btn-small btn-secondary btn-regenerate-answer"
                                onClick={() => handleRegenerateMockAnswer(index)}
                                disabled={saving || regeneratingAnswerIndex !== null || !(item.question || '').trim()}
                                title={!(item.question || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                              >
                                {regeneratingAnswerIndex === index ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                              </button>
                            </div>
                            <textarea
                              className="question-edit-textarea"
                              value={item.answer}
                              onChange={(e) => handleUpdateMockQuestion(index, 'answer', e.target.value)}
                              rows={3}
                              disabled={saving || regeneratingAnswerIndex === index}
                              placeholder="Введите ответ..."
                            />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn btn-primary"
                onClick={handleSubmitMockInterview}
                disabled={saving}
              >
                {saving ? '💾 Создание...' : '💾 Создать'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={handleCloseMockInterviewModal}
                disabled={saving}
              >
                Отмена
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MockInterviewGenerationSubTab;
